/**
 * Dynamic role definitions store.
 *
 * On startup, AuthContext fetches /api/role-definitions from the HOS backend,
 * which reads from the USM DB role_definitions table (editable via USM portal).
 * Until that fetch resolves, the static definitions from roles.ts are used as
 * a fallback so the app never blocks on the network call.
 */
import { ROLE_DEFINITIONS } from "./roles.ts";
import type { Permission } from "./types";

// roleKey → Permission[] — populated once on app init, refreshed on permission refresh
let _dynamicPerms: Record<string, string[]> | null = null;

/**
 * Called by AuthContext after a successful /api/role-definitions fetch.
 * Replaces the static fallback for all subsequent permission resolutions.
 */
export function setDynamicRolePermissions(perms: Record<string, string[]>): void {
  _dynamicPerms = perms;
}

/**
 * Returns the effective permissions for a role key.
 * Prefers dynamically loaded definitions; falls back to the static roles.ts data.
 */
export function getRolePermissions(roleKey: string): Permission[] {
  if (_dynamicPerms !== null && roleKey in _dynamicPerms) {
    return _dynamicPerms[roleKey] as Permission[];
  }
  return (ROLE_DEFINITIONS[roleKey as keyof typeof ROLE_DEFINITIONS]?.permissions ?? []) as Permission[];
}
